import type { InboxItem } from '@secretario/shared';
import { useSyncExternalStore } from 'react';
import { pertoClaims } from './pertoReplies.js';

/**
 * Conversas de outros apps que esperam uma decisão (responder, descartar ou deixar
 * para depois). Cada conversa junta as falas que chegaram em sequência ("oi", "tá
 * aí?", "me liga") num cartão só, e nada some enquanto a pessoa não decide.
 *
 * O texto das mensagens fica só na memória do app (como no ouvinte nativo); no
 * armazenamento do aparelho vai apenas até quando cada conversa já foi tratada
 * (chave e hora, sem conteúdo), para não reaparecer depois de respondida.
 */
export type InboxLine = { text: string; at: number };
export type InboxConversation = { key: string; item: InboxItem; lines: InboxLine[] };

const HANDLED_KEY = 'secretario.avisos.tratados';
const MAX_LINES = 6;
const MAX_HANDLED = 80;

const conversations = new Map<string, InboxConversation>();
const listeners = new Set<() => void>();
let snapshot: InboxConversation[] = [];

function readHandled(): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(HANDLED_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function writeHandled(handled: Record<string, number>): void {
  const entries = Object.entries(handled)
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_HANDLED);
  try {
    window.localStorage.setItem(HANDLED_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* sem armazenamento: vale só nesta abertura */
  }
}

function emit(): void {
  snapshot = [...conversations.values()].sort(
    (a, b) => (b.lines.at(-1)?.at ?? b.item.at) - (a.lines.at(-1)?.at ?? a.item.at),
  );
  listeners.forEach((listener) => listener());
}

/** As falas que o aviso trouxe (aviso antigo, sem a lista, vale como uma fala só). */
function linesOf(item: InboxItem): InboxLine[] {
  return item.lines && item.lines.length > 0
    ? item.lines.map((line) => ({ text: line.text, at: line.at || item.at }))
    : [{ text: item.text, at: item.at }];
}

/**
 * Junta o aviso à conversa dele. Devolve true quando trouxe fala nova (a conversa
 * entrou ou cresceu); false quando era repetido ou já tratado.
 */
export function addInbox(item: InboxItem): boolean {
  if (item.mine) {
    settleInbox(item.key, item.at);
    return false;
  }
  // A resposta de um pedido do Perto de Mim fica na página do pedido, não aqui.
  if (pertoClaims(item)) return false;
  const handledAt = readHandled()[item.key] ?? 0;
  const fresh = linesOf(item).filter((line) => line.at > handledAt);
  if (fresh.length === 0) return false;
  const current = conversations.get(item.key);
  const known = new Set((current?.lines ?? []).map((line) => `${line.at}|${line.text}`));
  const added = fresh.filter((line) => !known.has(`${line.at}|${line.text}`));
  if (current && added.length === 0) {
    // Mesmas falas: só atualiza o aviso (a resposta continua indo pelo mais novo).
    current.item = item;
    return false;
  }
  const lines = [...(current?.lines ?? []), ...added].sort((a, b) => a.at - b.at).slice(-MAX_LINES);
  conversations.set(item.key, { key: item.key, item, lines });
  emit();
  return true;
}

/** Respondida ou descartada: sai da fila e não volta com as mesmas falas. */
export function settleInbox(key: string, at?: number): void {
  const current = conversations.get(key);
  const last = at ?? current?.lines.at(-1)?.at ?? current?.item.at ?? Date.now();
  const handled = readHandled();
  handled[key] = Math.max(handled[key] ?? 0, last);
  writeHandled(handled);
  if (conversations.delete(key)) emit();
}

/** O aviso saiu da barra do Android (lido no próprio WhatsApp): a conversa sai daqui. */
export function keepOnlyInbox(keys: Set<string>): void {
  let changed = false;
  for (const key of [...conversations.keys()]) {
    if (!keys.has(key)) {
      conversations.delete(key);
      changed = true;
    }
  }
  if (changed) emit();
}

export const inboxConversation = (key: string) => conversations.get(key) ?? null;
export const pendingInbox = () => snapshot;

export function subscribeInbox(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** As conversas esperando, da mais nova para a mais antiga. */
export function usePendingInbox(): InboxConversation[] {
  return useSyncExternalStore(subscribeInbox, pendingInbox, pendingInbox);
}

/** Pedido para reabrir o cartão (a etiqueta da Início dispara). */
export const INBOX_OPEN_EVENT = 'secretario:inbox-open';
